import {
  IconMessageCircle,
  IconPlus,
  IconRocket,
  IconSettings,
  IconSparkles,
  IconTarget,
  IconTemplate,
  IconUsers,
} from "@tabler/icons-react";
import { memo, useCallback, useRef } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Row,
} from "react-bootstrap";
import Swal from "sweetalert2";
import SpecialFlowComponent from "./specialFlow";
import DynamicFlowStepComponent from "./dynamicFlowStep";
import { DraggableStepItem } from "../AgentWizard";

const StepConversation = memo(
  ({
    register,
    watch,
    setValue,
    flowMode,
    setFlowMode,
    handleAIFill,
    ImageSelect,
    CONVERSATION_TEMPLATES,
    applyTemplate,
    NEXT_STEP_OPIONS,
    addSpecialFlow,
    removeSpecialFlow,
    moveFlow,
    specialFlows,
    getFlowStepsForMode,
  }) => {
    // ✅ CORREÇÃO: Usar ref para controlar inicialização única
    const isInitializedRef = useRef(false);

    // ✅ CORREÇÃO: Acessar dados diretamente do formulário sem estado local
    const currentDefaultSteps = watch("defaultFlowSteps") || [];

    // ✅ CORREÇÃO: Função para obter etapas iniciais sem estado local
    const getInitialDefaultSteps = useCallback(() => {
      const existingSteps = watch("defaultFlowSteps");

      if (existingSteps && existingSteps.length > 0) {
        return existingSteps;
      }

      return [
        {
          id: "step_" + Date.now(),
          key: "greeting",
          label: "Saudação",
          icon: "👋",
          description: "Como o agente vai cumprimentar cada pessoa que chegar",
          placeholder:
            "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
          order: 0,
        },
      ];
    }, []); // ✅ CORREÇÃO: Sem dependências problemáticas

    // ✅ CORREÇÃO: Inicialização única sem useEffect problemático
    if (!isInitializedRef.current && currentDefaultSteps.length === 0) {
      const initialSteps = getInitialDefaultSteps();
      setValue("defaultFlowSteps", initialSteps, {
        shouldValidate: false,
        shouldDirty: false,
      });
      isInitializedRef.current = true;
    }

    // ✅ CORREÇÃO: Função para adicionar etapa sem estado local
    const addNewStep = useCallback(() => {
      const existingSteps = watch("defaultFlowSteps") || [];

      // ✅ CORREÇÃO: Usar ID único como chave para evitar colisões
      const uniqueId = "step_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      
      const newStep = {
        id: uniqueId,
        key: uniqueId, // ✅ CORREÇÃO: Chave única baseada no ID
        label: `Etapa ${existingSteps.length + 1}`,
        icon: "📝",
        description: "Nova etapa do atendimento",
        placeholder: "Digite o que o agente deve falar nesta etapa...",
        order: existingSteps.length,
      };

      const updatedSteps = [...existingSteps, newStep];
      setValue("defaultFlowSteps", updatedSteps, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }, [setValue]); // ✅ CORREÇÃO: Dependências estáveis

    // ✅ CORREÇÃO: Função para remover etapa sem estado local
    const removeStep = useCallback(
      (stepId) => {
        const existingSteps = watch("defaultFlowSteps") || [];

        if (existingSteps.length <= 1) {
          Swal.fire({
            icon: "warning",
            title: "Não é possível remover",
            text: "Deve haver pelo menos uma etapa no fluxo.",
          });
          return;
        }

        Swal.fire({
          title: "Excluir etapa?",
          text: "Esta ação não poderá ser desfeita.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, excluir",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc3545",
        }).then((result) => {
          if (result.isConfirmed) {
            const stepToRemove = existingSteps.find((step) => step.id === stepId);
            const newSteps = existingSteps.filter((step) => step.id !== stepId);
            const reorderedSteps = newSteps.map((step, index) => ({
              ...step,
              order: index,
            }));

            // ✅ CORREÇÃO: Limpar campos do React Hook Form para evitar dados fantasma
            if (stepToRemove) {
              const fieldKey = `defaultFlow.${stepToRemove.key}`;
              const nextStepKey = `defaultFlow.${stepToRemove.key}NextStep`;
              
              // Limpar os valores dos campos removidos
              setValue(fieldKey, undefined, { shouldValidate: false });
              setValue(nextStepKey, undefined, { shouldValidate: false });
              
            }

            setValue("defaultFlowSteps", reorderedSteps, {
              shouldValidate: false,
              shouldDirty: false,
            });
          }
        });
      },
      [setValue]
    ); // ✅ CORREÇÃO: Dependências estáveis

    // ✅ CORREÇÃO: Função para mover etapas sem estado local
    const moveDefaultFlowStep = useCallback(
      (dragIndex, hoverIndex) => {
        const existingSteps = watch("defaultFlowSteps") || [];
        const newSteps = [...existingSteps];
        const draggedStep = newSteps[dragIndex];

        newSteps.splice(dragIndex, 1);
        newSteps.splice(hoverIndex, 0, draggedStep);

        const reorderedSteps = newSteps.map((step, index) => ({
          ...step,
          order: index,
        }));

        setValue("defaultFlowSteps", reorderedSteps, {
          shouldValidate: false,
          shouldDirty: false,
        });
      },
      [setValue] // ✅ CORREÇÃO: Dependências estáveis
    );

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <IconMessageCircle className="me-2 text-primary" size={24} />
            <h4 className="mb-0">Como seu Agente vai Conversar</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <ButtonGroup>
              <Button
                variant={flowMode === "basic" ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => setFlowMode("basic")}
              >
                <IconUsers className="me-1" size={16} />
                Básico
              </Button>
              <Button
                variant={
                  flowMode === "advanced" ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setFlowMode("advanced")}
              >
                <IconSettings className="me-1" size={16} />
                Avançado
              </Button>
            </ButtonGroup>
          </div>
        </div>

        {flowMode === "basic" && (
          <Alert variant="info" className="mb-4">
            <IconUsers className="me-2" size={16} />
            <strong>Modo Básico:</strong> Configure as conversas de forma
            simples e intuitiva. Perfeito para quem está começando! 😊
          </Alert>
        )}

        {flowMode === "advanced" && (
          <Alert variant="warning" className="mb-4">
            <IconSettings className="me-2" size={16} />
            <strong>Modo Avançado:</strong> Controle total sobre fluxos e
            situações especiais. Para usuários experientes.
          </Alert>
        )}

        {/* Templates Section - Only in Basic Mode */}
        {/* {flowMode === "basic" && (
          <div className="mb-4">
            <h6 className="mb-3">
              <IconTemplate className="me-2" size={16} />
              Templates Prontos para Usar
            </h6>
            <Row>
              {Object.entries(CONVERSATION_TEMPLATES).map(([key, template]) => (
                <Col md={4} key={key} className="mb-3">
                  <Card
                    className="h-100 border-2 position-relative"
                    style={{ cursor: "pointer" }}
                    onClick={() => applyTemplate(key)}
                  >
                    <Card.Body className="text-center">
                      <div className="fs-2 mb-2">{template.icon}</div>
                      <h6 className="mb-2">{template.name}</h6>
                      <p className="small text-muted mb-3">
                        {template.description}
                      </p>
                      <Badge
                        bg="primary"
                        className="position-absolute top-0 end-0 m-2"
                      >
                        {template.steps.length} etapas
                      </Badge>
                      <Button variant="outline-primary" size="sm">
                        <IconSparkles className="me-1" size={14} />
                        Usar Template
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )} */}

        {/* Main Flow Configuration */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="mb-0">
              <IconTarget className="me-2" size={16} />
              {flowMode === "basic" ? "Agente Principal" : "Agente Padrão"}
            </h6>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={addNewStep}>
                <IconPlus className="me-1" size={16} />
                Nova Etapa
              </Button>
              {flowMode === "advanced" && (
                <Button variant="primary" size="sm" onClick={addSpecialFlow}>
                  <IconPlus className="me-1" size={16} />
                  Novo Fluxo Especial
                </Button>
              )}
            </div>
          </div>

          {/* ✅ CORREÇÃO: Usar currentDefaultSteps diretamente do watch */}
          <div className="mb-4">
            {currentDefaultSteps.map((step, index) => (
              <DraggableStepItem
                key={step.id}
                step={step}
                index={index}
                moveStep={moveDefaultFlowStep}
              >
                <DynamicFlowStepComponent
                  step={step}
                  index={index}
                  flowType="default"
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  handleAIFill={handleAIFill}
                  ImageSelect={ImageSelect}
                  NEXT_STEP_OPIONS={NEXT_STEP_OPIONS}
                  flowMode={flowMode}
                  onRemove={() => removeStep(step.id)}
                  canRemove={currentDefaultSteps.length > 1}
                />
              </DraggableStepItem>
            ))}
          </div>
        </div>

        {/* Special Flows - Only in Advanced Mode */}
        {flowMode === "advanced" && specialFlows.length > 0 && (
          <div className="mt-4">
            <h6 className="mb-3">
              <IconRocket className="me-2" size={16} />
              Sub Agentes
            </h6>
            {specialFlows.map((flow, index) => (
              <SpecialFlowComponent
                key={`flow-${index}`}
                flow={flow}
                index={index}
                moveFlow={moveFlow}
                removeSpecialFlow={removeSpecialFlow}
                register={register}
                watch={watch}
                setValue={setValue}
                handleAIFill={handleAIFill}
                ImageSelect={ImageSelect}
                NEXT_STEP_OPIONS={NEXT_STEP_OPIONS}
                getFlowStepsForMode={getFlowStepsForMode}
              />
            ))}
          </div>
        )}

        {flowMode === "advanced" && specialFlows.length === 0 && (
          <div className="text-center py-5 text-muted mt-4">
            <IconSettings size={48} className="mb-3" />
            <p>Nenhum fluxo especial configurado.</p>
            <p>
              Clique em "Novo Fluxo Especial" para criar fluxos personalizados
              para situações específicas.
            </p>
          </div>
        )}

       
      </div>
    );
  }
);

StepConversation.displayName = "StepConversation";

export default StepConversation;
